// Engasjementsregler (RoE) + Check-Fire/Splash. Den harde porten FØR ild:
// gitt gjeldende våpenholdning (hold/tight/free) og målets status (venn/fiende/
// ukjent, positivt identifisert, fiendtlig handling) → er engasjement tillatt,
// og krever det godkjenning? Pluss splash-nedtelling + check-fire-abort. Ren,
// testbar logikk; kompletterer firecoord.ts (geo-deconfliction) + fireapproval.ts
// (menneskesignert godkjenning).
import { z } from 'zod';
/** Våpenholdning (RoE-posture), fra mest til minst restriktiv. */
export const WeaponsPosture = z.enum(['hold', 'tight', 'free']);
export const POSTURE_LABEL = {
    hold: 'Weapons HOLD (kun selvforsvar)',
    tight: 'Weapons TIGHT (kun positivt id. fiende)',
    free: 'Weapons FREE (alt som ikke er positivt venn)',
};
/**
 * Vurder engasjement mot RoE. Rekkefølge: fratricide-vern → selvforsvar →
 * holdnings-regel. `requiresApproval` settes ved danger-close/høy CDE (håndheves
 * så av fireapproval.canRelease).
 */
export function assessRoe(posture, input) {
    const requiresApproval = !!input.dangerClose || !!input.cdeHigh;
    const approvalNote = requiresApproval ? ' (krever kommandant-godkjenning)' : '';
    // Fratricide-vern: aldri ild mot egne/venn.
    if (input.affiliation === 'friend') {
        return { authorized: false, requiresApproval, reason: 'Eget/venn — ild NEKTET (fratricide-vern)' };
    }
    // Selvforsvar gjelder uansett holdning.
    if (input.hostileAct) {
        return { authorized: true, requiresApproval, reason: `Selvforsvar mot fiendtlig handling${approvalNote}` };
    }
    switch (posture) {
        case 'hold':
            return { authorized: false, requiresApproval, reason: 'Weapons HOLD — ild kun ved fiendtlig handling (selvforsvar)' };
        case 'tight':
            if (input.affiliation === 'hostile' && input.pid) {
                return { authorized: true, requiresApproval, reason: `Positivt identifisert fiende${approvalNote}` };
            }
            return { authorized: false, requiresApproval, reason: input.affiliation === 'hostile' ? 'Weapons TIGHT — mangler PID (positiv id.)' : 'Weapons TIGHT — ikke identifisert som fiende' };
        case 'free':
            // Fri ild mot alt som ikke er positivt venn — men aldri nøytral.
            if (input.affiliation === 'neutral') {
                return { authorized: false, requiresApproval, reason: 'Weapons FREE — men målet er nøytralt' };
            }
            return { authorized: true, requiresApproval, reason: `Weapons FREE — ${input.affiliation === 'hostile' ? 'fiende' : 'ikke-venn'}${approvalNote}` };
    }
}
// ── Check-Fire / Splash-nedtelling ──────────────────────────────────────────
/** Sekunder til nedslag (splash). shotMs = avfyringstid, tofS = flytid (s). */
export function splashCountdownS(nowMs, shotMs, tofS) {
    return Math.max(0, (shotMs + tofS * 1000 - nowMs) / 1000);
}
/** «Splash» ropes typisk ~5 s før nedslag. Er vi innenfor varslingsvinduet? */
export function isSplashCall(nowMs, shotMs, tofS, windowS = 5) {
    const left = splashCountdownS(nowMs, shotMs, tofS);
    return left > 0 && left <= windowS;
}
/** Har nedslag inntruffet? */
export function hasImpacted(nowMs, shotMs, tofS) {
    return nowMs >= shotMs + tofS * 1000;
}
/**
 * Check-Fire: kan ilden fortsatt stanses? Kun før nedslag — etter splash er det
 * for sent (rundene er i lufta/nede). Returnerer om abort er effektiv nå.
 */
export function canCheckFire(nowMs, shotMs, tofS) {
    return !hasImpacted(nowMs, shotMs, tofS);
}
//# sourceMappingURL=roe.js.map